import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFieldArray, useForm } from 'react-hook-form';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { MODE, MAX_ITEM_COUNT_IN_TABLE } from 'pages/TableOrdering/config';
import {
  createEmployeesTO,
  deleteEmployeesTO,
  getEmployeesOrderingTO,
  updateEmployeesTO,
} from 'redux/actions/TableOrdering/employees';
import { useDispatch, useSelector } from 'react-redux';
import { LAST_CREATED_EMPLOYEE_TO } from 'redux/actions/types';
import { getJobsOrderingTO } from 'redux/actions/TableOrdering/jobs';
import { getUserData } from 'redux/actions/user';
import { employeeSchema } from '../Employees/validationSchema';
import { useEmployeeColumns } from './useEmployeeColumns';

const DEFAULT_FORM_VALUES = {
  firstname: '',
  lastname: '',
  pinCode: '',
  phone: '',
  email: '',
  businesses: [
    {
      businessUuid: '',
      jobs: [{ _id: '', pay: null }],
    },
  ],
};

export const useTableOrderingEmployee = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [modalState, setModalState] = useState({
    open: false,
    mode: MODE.CREATE,
    showPinCode: false,
  });

  const [employeeState, setEmployeeState] = useState({
    selectedEmployee: null,
    openBusinessesModal: false,
    businessAvailableJobs: [],
  });

  const [rowsPerPage, setRowsPerPage] = useState(MAX_ITEM_COUNT_IN_TABLE);
  const [makeDisabled, setMakeDisabled] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState(null);
  const [loadingRowBusinessId, setLoadingRowBusinessId] = useState(null);
  const {
    employees = [],
    loading,
    lastCreatedEmployee,
    jobs,
  } = useSelector(({ tableOrdering }) => tableOrdering);

  const { currentBusiness } = useSelector(({ business }) => business);
  const { allBusinessList } = useSelector(({ user }) => user);

  const formMethods = useForm({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'all',
    resolver: yupResolver(employeeSchema),
  });

  const { control, reset, setValue, getValues, formState } = formMethods;
  const { isDirty } = formState;

  const businesses = useFieldArray({
    control,
    name: 'businesses',
  });

  console.log(getValues(), 'getValues');

  const currentModel = useMemo(() => getValues(), [getValues]);
  const visiblePinCode = () =>
    setModalState((prevState) => ({
      ...prevState,
      showPinCode: !prevState.showPinCode,
    }));

  const isSubmitDisabled = useMemo(() => {
    return !isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (currentBusiness?._id) {
      dispatch(getEmployeesOrderingTO(currentBusiness?.uuid));
      dispatch(getJobsOrderingTO(currentBusiness?.uuid));
      dispatch(getUserData());
    }
  }, [currentBusiness?._id]);

  useEffect(() => {
    return () => {
      dispatch({ type: LAST_CREATED_EMPLOYEE_TO, payload: null });
    };
  }, []);

  const handleModalState = useCallback(
    (updates) => {
      if (updates.open && updates.mode === MODE.CREATE) {
        const currentBusinessUuid = currentBusiness?.uuid;
        if (currentBusinessUuid) {
          setValue('businesses[0].businessUuid', currentBusinessUuid, {
            shouldDirty: true,
            shouldValidate: true,
          });

          businesses.replace([
            {
              businessUuid: currentBusinessUuid,
              jobs: [{ _id: '', pay: null }],
            },
          ]);

          dispatch(getJobsOrderingTO(currentBusinessUuid, true)).then(
            (response) => {
              setEmployeeState((prev) => ({
                ...prev,
                businessAvailableJobs: [
                  {
                    [currentBusinessUuid]: response,
                  },
                ],
              }));
            }
          );
        }
      }
      setModalState((prev) => ({ ...prev, ...updates }));
    },
    [currentBusiness?.uuid, setValue, dispatch]
  );

  const handleEmployeeState = useCallback((updates) => {
    setEmployeeState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleCloseBusinessesModal = useCallback(() => {
    setEmployeeState((prev) => ({ ...prev, openBusinessesModal: false }));
    setLoadingRowBusinessId(null);
    setLoadingRowId(null);
  }, []);

  const handleBusinessChange = useCallback(
    async (event, index) => {
      try {
        dispatch(getJobsOrderingTO(event.target.value, true)).then(
          (response) => {
            const businessUuid = event.target.value;

            const businessAvailableJobs = {
              [businessUuid]: response,
            };

            setEmployeeState((prev) => {
              const existingJobs = prev?.businessAvailableJobs || [];

              const newBusinessUuid = event.target.value;

              const filteredJobs = existingJobs.filter(
                (item) => !Object.keys(item).includes(newBusinessUuid)
              );

              const newEntry = {
                [newBusinessUuid]: response,
              };

              return {
                ...prev,
                businessAvailableJobs: [...filteredJobs, newEntry],
              };
            });
          }
        );

        setValue(`businesses[${index}].businessUuid`, event.target.value, {
          shouldDirty: true,
          shouldValidate: true,
        });

        const currentValues = getValues();
        const updatedBusinesses = currentValues.businesses.map(
          (business, i) => {
            if (i === index) {
              return {
                ...business,
                businessUuid: event.target.value,
                jobs: [{ _id: '', pay: null }],
              };
            }
            return business;
          }
        );

        reset(
          {
            ...currentValues,
            businesses: updatedBusinesses,
          },
          { keepDirty: true }
        );
        setMakeDisabled(true);
      } catch (error) {
        console.error('Failed to update jobs for the selected business', error);
      }
    },
    [setValue, reset, getValues, setEmployeeState]
  );

  useEffect(() => {
    if (!modalState.open) {
      setEmployeeState((prev) => ({ ...prev, businessAvailableJobs: [] }));
      setMakeDisabled(false);
    }
  }, [modalState.open]);

  const handleJobChange = useCallback(
    (event, index) => {
      const value = event?.target?.value ? String(event.target.value) : '';
      // Ensure the jobs array exists with at least one object
      const current = getValues(`businesses[${index}].jobs`) || [];
      if (current.length === 0) {
        setValue(`businesses[${index}].jobs`, [{ _id: '', pay: null }], {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
      // Update only the nested _id so watchers pick it up immediately

      setValue(`businesses[${index}].jobs`, [{ _id: value, pay: null }], {
        shouldDirty: true,
        shouldValidate: false,
      });
      setMakeDisabled(false);
    },
    [setValue, reset, getValues]
  );

  const handleSubmit = useCallback(
    async (values) => {
      const requestData = {
        firstname: values?.firstname || '',
        lastname: values?.lastname || '',
        phone: values?.phone || '',
        email: values?.email || '',
        pinCode: values?.pinCode || '',
        businesses: (values?.businesses || []).map((b) => ({
          businessUuid: b.businessUuid,
          jobs: (b.jobs || []).filter((j) => j._id),
        })),
      };
      if (formState.errors?.length) {
        return;
      }

      try {
        if (modalState.mode === MODE.EDIT) {
          await dispatch(
            updateEmployeesTO(
              requestData,
              values?.employeeId,
              currentBusiness?.uuid
            )
          );
        } else {
          await dispatch(createEmployeesTO(requestData, currentBusiness?.uuid));
        }
        handleModalState({
          open: false,
          mode: MODE.CREATE,
          showPinCode: false,
        });
        setTimeout(() => {
          handleCloseBusinessesModal();
        }, 1000);
        reset(DEFAULT_FORM_VALUES);
      } catch (error) {
        console.error('Error submitting employee:', error);
      }
    },
    [modalState.mode, currentBusiness?.uuid, reset]
  );

  const deleteEmployee = async (data) => {
    const confirmed = await confirm({
      title: t('Confirm Employee Deletion'),
      message: t('Are you sure you want to delete this employee?'),
      cancelText: t('Cancel'),
      confirmText: t('Delete'),
      confirmColor: 'primary',
      background: 'rgba(226, 21, 8, 1)',
    });
    if (confirmed) {
      dispatch(deleteEmployeesTO(data?.id, currentBusiness?.uuid));
    }
  };

  const handleEdit = (data) => {
    const editBusinesses = data?.businesses?.map((business) => ({
      businessUuid: business.businessUuid,
      jobs: business.jobs.map((job) => ({
        _id: job._id,
        pay: job.pay,
      })),
    }));

    const updatedData = {
      ...data,
      businesses:
        editBusinesses?.length > 0
          ? editBusinesses
          : [
              {
                businessUuid: '',
                jobs: [{ _id: '', pay: null }],
              },
            ],
    };

    reset(updatedData);
    handleModalState({ open: true, mode: MODE.EDIT });
  };

  const handleCancel = () => {
    reset(DEFAULT_FORM_VALUES);
    businesses.replace([
      {
        businessUuid: '',
        jobs: [{ _id: '', pay: null }],
      },
    ]);
    handleModalState({ open: false, mode: MODE.EDIT });
    formMethods.clearErrors();
    setTimeout(() => {
      handleModalState({
        open: false,
        mode: MODE.CREATE,
        showPinCode: false,
      });
    }, 500);
  };

  const handleAddNewJob = () => {
    businesses.append({
      businessUuid: '',
      jobs: [{ _id: '', pay: null }],
    });
    setMakeDisabled(false);
  };

  const handleRemove = (index) => {
    setMakeDisabled(false);
    businesses.remove(index);
  };

  // Separate async function to load jobs
  const loadJobsForBusinesses = async (
    businesses,
    employeeState,
    dispatch,
    setEmployeeState
  ) => {
    const fetchedUuids = new Set();
    const fetchPromises = [];

    businesses?.forEach((business) => {
      const businessUuid = business.businessUuid;

      if (fetchedUuids.has(businessUuid)) return;

      const alreadyExists = employeeState.businessAvailableJobs.some((item) =>
        Object.prototype.hasOwnProperty.call(item, businessUuid)
      );

      if (!alreadyExists) {
        fetchedUuids.add(businessUuid);

        const fetchPromise = (async () => {
          try {
            const response = await dispatch(
              getJobsOrderingTO(businessUuid, true)
            );

            const businessAvailableJobs = {
              [businessUuid]: response,
            };

            setEmployeeState((prev) => {
              const existingJobs = [...prev.businessAvailableJobs];
              const filteredJobs = existingJobs.filter(
                (item) => !Object.keys(item).includes(businessUuid)
              );

              return {
                ...prev,
                businessAvailableJobs: [...filteredJobs, businessAvailableJobs],
              };
            });
          } catch (error) {
            setLoadingRowId(null);
            setLoadingRowBusinessId(null);
            console.error(
              `Failed to fetch jobs for business UUID: ${businessUuid}`,
              error
            );
            // Optional: You can store the error state, show a toast, or retry
          }
        })();

        fetchPromises.push(fetchPromise);
      }
    });

    await Promise.all(fetchPromises);
  };

  const handleEditCurrentEmployee = async (data) => {
    setLoadingRowId(data?.id);
    await loadJobsForBusinesses(
      data?.businesses,
      employeeState,
      dispatch,
      setEmployeeState
    );

    // Now safe to reset and open modal
    reset(data);
    handleModalState({ open: true, mode: MODE.EDIT });
    setLoadingRowId(null);
  };

  const handleEditRow = async (row) => {
    setLoadingRowBusinessId(row?.id);
    await loadJobsForBusinesses(
      row?.businesses,
      employeeState,
      dispatch,
      setEmployeeState
    );

    handleEmployeeState({
      selectedEmployee: row,
      openBusinessesModal: true,
    });
    setLoadingRowBusinessId(null);
  };

  const columns = useEmployeeColumns({
    t,
    allBusinessList,
    handleEmployeeState,
    handleEdit: (data) => {
      handleEditCurrentEmployee(data);
    },
    handleDelete: async (data) => {
      const confirmed = await confirm({
        title: t('Confirm Employee Deletion'),
        message: t('Are you sure you want to delete this employee?'),
        cancelText: t('Cancel'),
        confirmText: t('Delete'),
        confirmColor: 'primary',
        background: 'rgba(226, 21, 8, 1)',
      });
      if (confirmed) {
        dispatch(deleteEmployeesTO(data?.id, currentBusiness?.uuid));
      }
    },
    handleEditRow,
    loadingRowId,
    loadingRowBusinessId,
  });

  return {
    formMethods,
    businesses,
    businessesFields: businesses.fields,
    isSubmitDisabled,
    modalState,
    jobs,
    employeeState,
    employees: useMemo(
      () =>
        employees.map((employ) => ({
          id: employ._id,
          firstname: employ.firstname || '',
          lastname: employ.lastname || '',
          pinCode: employ.pinCode || '',
          email: employ?.email || '',
          phone: employ?.phone || '',
          businesses: employ?.businesses || [],
          employeeId: employ?._id || '-',
        })),
      [employees]
    ),
    columns,
    loading,
    visiblePinCode,
    lastCreatedEmployee,
    rowsPerPage,
    handleBusinessChange,
    handleJobChange,
    handleSubmit,
    handleAddNewJob,
    handleRemove,
    handleModalState,
    handleEdit,
    handleCancel,
    handleEmployeeState,
    setRowsPerPage,
    deleteEmployee,
    handleCloseBusinessesModal,
    businessAvailableJobs: employeeState.businessAvailableJobs,
    makeDisabled,
  };
};
